import { TranslationKeys } from "../types";
import { SITE_CONFIG } from "../../app/config";

export const en: TranslationKeys = {
  hero: {
    title1: "OWN YOUR",
    title2: "SOUND",
    streamingRenting: "STREAMING",
    vinylCdForever: "VINYL & CD",
    description: `Paste your Spotify playlist. Get a shopping list of Vinyl & CDs. Actually own your music. Build a collection that lasts.`,
    ctaName: SITE_CONFIG.name,
  },
  
  perks: {
    ownIt: {
      title: "Actually Yours",
      desc: "No monthly fees. No disappearing tracks. Buy once, keep forever. Real ownership.",
    },
    quality: {
      title: "Physical Hits Different",
      desc: "Analog warmth or uncompressed digital audio. Streaming just can't compete.",
    },
    collection: {
      title: "Show It Off",
      desc: "Shelf goals. Wall art. Real music you can hold, not just a file in the cloud.",
    },
  },
  
  form: {
    placeholder: "https://open.spotify.com/playlist/37i9dQZF1DXcBWIGoYBM5M",
    go: "GO",
    tryExample: "No playlist? Try an example",
    errors: {
      emptyPlaylist: "Paste a Spotify playlist URL or ID to continue.",
      cloudflareCheck: "Complete the Cloudflare check to continue.",
      cloudflareValidation: "Cloudflare validation failed. Try again.",
    },
  },
  
  results: {
    scanning: "SCANNING PLAYLIST",
    yourHaul: "YOUR VINYL & CD HAUL",
    playlist: "Playlist",
    tracksToAlbums: "tracks → albums to own",
    track: "track",
    tracks: "tracks",
    oops: "OOPS!",
    unableToProcess: "Unable to process playlist.",
    noAlbumsFound: "NO ALBUMS FOUND",
    noAlbumsDesc: "No albums found for this playlist!",
    tryDifferent: "Try a different playlist with more tracks.",
    albumsToOwn: "ALBUMS TO OWN",
    comingSoon: "Coming soon",
    ownIt: "OWN IT →",
  },
  
  share: {
    shareHaul: "🔥 SHARE YOUR HAUL",
    linkCopied: "🔗 LINK COPIED!",
    shared: "🚀 SHARED!",
    shareText: `🔥 I'm building my vinyl & CD collection! Check out my haul for "{playlistName}" 💿🎵 #vinylcollection #cdcollection #ownthemusic`,
  },
  
  affiliate: {
    disclosure: "Affiliate Disclosure:",
    disclosureText: `${SITE_CONFIG.name} is a participant in the ${SITE_CONFIG.affiliatePartner} affiliate program. We earn commissions on qualifying purchases made through our links, at no additional cost to you. This helps us keep ${SITE_CONFIG.name} free and running!`,
    notice: `Disclosure: Links below are affiliate links. We may earn a commission on purchases made through these links, at no extra cost to you. This helps support ${SITE_CONFIG.name}!`,
  },
  
  faq: {
    title: "Frequently Asked Questions",
    subtitle: "EVERYTHING YOU NEED TO KNOW ABOUT OWNING YOUR MUSIC.",
    sponsored: "Sponsored",
    adTitle: "Discover More Music Gear",
    adDesc: "Premium vinyl players, CD cases & more",
    items: [
      {
        question: `How does ${SITE_CONFIG.name} work?`,
        answer: "Paste any public Spotify playlist URL into GoOffline. We scan every track, match them to physical releases — vinyl records, CDs, and cassettes — and show you where to buy them with real prices from Discogs. One click from streaming to owning.",
      },
      {
        question: `Is ${SITE_CONFIG.name} free to use?`,
        answer: "Yes, GoOffline is completely free. No sign-up, no subscription, no hidden fees. We earn a small commission when you buy through our affiliate links, which costs you nothing extra and keeps the tool running.",
      },
      {
        question: "Do I need a Spotify account or login?",
        answer: "No account or login required. Just paste a public Spotify playlist link or share URL. We never access your Spotify account — we only read the tracks listed in the playlist you share.",
      },
      {
        question: "What music formats can I find — vinyl, CD, or cassette?",
        answer: "GoOffline searches for vinyl records, CDs, and cassette tapes. Each album card shows which formats are available along with current market prices. Vinyl and CD are the most common, while cassette availability varies by title.",
      },
      {
        question: "Does this work with private Spotify playlists?",
        answer: "Currently only public playlists are supported. Quick workaround: set your playlist to public, scan it on GoOffline, then switch it back to private. Takes about 10 seconds.",
      },
      {
        question: "Where do the prices come from?",
        answer: "All prices come from Discogs, the world's largest marketplace for vinyl records and CDs with over 80 million listings. We show estimated prices based on real market data so you get a reliable ballpark before you buy.",
      },
      {
        question: "How accurate are the vinyl and CD prices?",
        answer: "Prices are estimated from recent Discogs marketplace data and reflect real-world market conditions. The actual price may vary depending on the pressing, condition, and seller. Think of it as a reliable starting point for budgeting your collection.",
      },
      {
        question: "Can I share my vinyl and CD shopping list?",
        answer: "Yes! Hit the share button to send your haul to friends or post it on social media. Each result gets a unique shareable link. Sharing helps us stay free and helps your friends discover physical music.",
      },
      {
        question: "What if an album isn't found?",
        answer: "GoOffline works best with well-known releases — classic rock, jazz, hip-hop, pop hits, and legacy catalogs tend to have the highest match rates. Newer indie or digital-only releases may not have physical pressings yet. The more iconic the album, the better the results.",
      },
      {
        question: "Why buy vinyl or CDs in 2026?",
        answer: "Vinyl sales have grown for 18 consecutive years and hit record highs. CDs are making a comeback too, offering lossless audio quality at bargain prices. Physical music means real ownership — no subscriptions, no disappearing catalogs, and actual shelf presence.",
      },
      {
        question: "Vinyl vs CD — which format should I buy?",
        answer: "Vinyl offers warm analog sound, collector value, large 12-inch artwork, and makes a great display piece. CDs are more affordable, portable, deliver perfect digital audio, and are easier to find. Both are better than paying monthly to rent music that can disappear from streaming platforms.",
      },
      {
        question: `Is ${SITE_CONFIG.name} affiliated with Spotify?`,
        answer: "No. GoOffline is an independent tool built for music fans who want to own their collection. We use Spotify's public API to read playlist data but are not endorsed by or affiliated with Spotify in any way.",
      },
      {
        question: `Is my data private and safe on ${SITE_CONFIG.name}?`,
        answer: "Absolutely. We don't require any login or personal information. We only read the track data from the public playlist URL you provide. No tracking cookies, no data selling. Your privacy is fully respected.",
      },
      {
        question: "Why own physical music instead of streaming?",
        answer: "Streaming services can remove albums without notice when licenses expire. Physical music is yours forever — no algorithms deciding what you hear, no subscription fatigue, no corporate decisions controlling your library. Plus there's nothing like the ritual of dropping a needle on vinyl or holding album artwork in your hands.",
      },
    ],
  },
  
  footer: {
    affiliateDisclosure: "Affiliate Disclosure:",
    copyright: `© ${new Date().getFullYear()} ${SITE_CONFIG.name} — Spotify to Vinyl & CD Converter`,
    converter: "Spotify to Vinyl & CD Converter",
    terms: "Terms of Service",
    privacy: "Privacy Policy",
    affiliateLink: "Affiliate Disclosure",
    notAffiliated: "Not affiliated with Spotify",
  },
  
  ad: {
    label: "AD",
    title: "Your Ad Here",
    desc: "Responsive Display Ad · 728×90 or similar",
  },
  
  language: {
    label: "Language",
    en: "English",
    fr: "Français",
    de: "Deutsch",
    pl: "Polski",
  },
};
